import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const HindsightSimulator = () => {
  // State management for form inputs
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [monthlyContribution, setMonthlyContribution] = useState('');
  const [frequency, setFrequency] = useState('monthly');
  
  // State for results
  const [simulationResults, setSimulationResults] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handler function for simulation
  const handleRunSimulation = async () => {
    // Basic validation to ensure inputs are not empty
    if (!startDate || !endDate || !monthlyContribution) {
      setError('Please fill in all fields.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSimulationResults(null);

    try {
      // Construct the URL with query parameters
      const params = new URLSearchParams({
        startDate,
        endDate,
      });
      const response = await fetch(`/api/getHistoricalData?${params}`);

      if (!response.ok) {
        // If the server responds with an error, throw it to the catch block
        const errorData = await response.json();
        throw new Error(errorData.error || 'Something went wrong');
      }

      const data = await response.json();
      
      // Process the data for DCA simulation
      const contribution = parseFloat(monthlyContribution);
      let totalInvested = 0;
      let totalBitcoin = 0;
      const portfolioHistory = [];
      
      // Determine contribution frequency in days
      let daysBetweenPurchases = 30; // default monthly
      if (frequency === 'weekly') daysBetweenPurchases = 7;
      if (frequency === 'quarterly') daysBetweenPurchases = 90;
      
      // Process each data point
      let lastPurchaseDate = null;
      
      data.forEach(([timestamp, price], index) => {
        const currentDate = new Date(timestamp);
        
        // Check if it's time to make a purchase
        if (!lastPurchaseDate || 
            (currentDate - lastPurchaseDate) / (1000 * 60 * 60 * 24) >= daysBetweenPurchases) {
          
          // Make a purchase
          const bitcoinPurchased = contribution / price;
          totalBitcoin += bitcoinPurchased;
          totalInvested += contribution;
          lastPurchaseDate = currentDate;
        }
        
        // Calculate current portfolio value
        const portfolioValue = totalBitcoin * price;
        
        // Add to chart data
        portfolioHistory.push({
          date: currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
          invested: Math.round(totalInvested),
          value: Math.round(portfolioValue),
          price: Math.round(price),
          btc: totalBitcoin.toFixed(8)
        });
      });
      
      // Calculate final metrics
      const finalData = portfolioHistory[portfolioHistory.length - 1];
      const totalReturn = ((finalData.value - finalData.invested) / finalData.invested) * 100;
      const totalProfit = finalData.value - finalData.invested;
      
      setSimulationResults({
        totalInvested: finalData.invested,
        finalValue: finalData.value,
        totalBitcoin: totalBitcoin.toFixed(8),
        totalReturn: totalReturn.toFixed(2),
        totalProfit: totalProfit,
        averageCost: (finalData.invested / totalBitcoin).toFixed(2)
      });
      
      setChartData(portfolioHistory);
      setIsLoading(false);

    } catch (error) {
      console.error('API Call Failed:', error.message);
      setError(error.message || 'Failed to run simulation. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 bg-gray-900 text-white min-h-screen">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6 text-orange-500">Hindsight Simulator</h1>
        
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-2xl font-semibold mb-4 text-white">Simulation Parameters</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            
            {/* Contribution Frequency Dropdown */}
            <div>
              <label htmlFor="frequency" className="block text-sm font-medium text-gray-300 mb-1">Frequency</label>
              <select
                id="frequency"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>

            {/* Start Date Input */}
            <div>
              <label htmlFor="start-date" className="block text-sm font-medium text-gray-300 mb-1">Start Month (YYYY-MM)</label>
              <input
                type="month"
                id="start-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            {/* End Date Input */}
            <div>
              <label htmlFor="end-date" className="block text-sm font-medium text-gray-300 mb-1">End Month (YYYY-MM)</label>
              <input
                type="month"
                id="end-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            {/* Contribution Input with Dynamic Label */}
            <div>
              <label htmlFor="monthly-contribution" className="block text-sm font-medium text-gray-300 mb-1">{`${frequency.charAt(0).toUpperCase() + frequency.slice(1)} Contribution ($)`}</label>
              <input
                type="number"
                id="monthly-contribution"
                value={monthlyContribution}
                onChange={(e) => setMonthlyContribution(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="e.g., 500"
              />
            </div>

          </div>
          
          <div className="mt-6">
            <button
              onClick={handleRunSimulation}
              disabled={isLoading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-4 rounded-md transition duration-300 ease-in-out disabled:bg-gray-500 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Calculating...' : 'Run Hindsight Simulation'}
            </button>
          </div>
        </div>
        
        {/* Loading, Error, and Results Sections */}
        <div className="mt-8">
          {isLoading && (
            <div className="text-center">
              <p className="text-lg text-orange-500">Loading historical data and running simulation...</p>
            </div>
          )}

          {error && (
            <div className="text-center p-4 bg-red-900 border border-red-700 rounded-lg">
              <p className="text-lg font-semibold text-white">An Error Occurred</p>
              <p className="text-red-300">{error}</p>
            </div>
          )}

          {simulationResults && (
          <div className="mt-8 space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-800 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-400 mb-1">Total Invested</h3>
                <p className="text-2xl font-bold text-white">
                  ${simulationResults.totalInvested.toLocaleString()}
                </p>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-400 mb-1">Final Value</h3>
                <p className="text-2xl font-bold text-green-500">
                  ${simulationResults.finalValue.toLocaleString()}
                </p>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-400 mb-1">Total Return</h3>
                <p className={`text-2xl font-bold ${simulationResults.totalReturn >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {simulationResults.totalReturn}%
                </p>
              </div>
            </div>
            
            {/* Additional Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-800 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-400 mb-1">Total Bitcoin</h3>
                <p className="text-xl font-bold text-orange-500">
                  ₿ {simulationResults.totalBitcoin}
                </p>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-400 mb-1">Average Cost</h3>
                <p className="text-xl font-bold text-white">
                  ${simulationResults.averageCost}
                </p>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-400 mb-1">Total Profit</h3>
                <p className={`text-xl font-bold ${simulationResults.totalProfit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  ${Math.abs(simulationResults.totalProfit).toLocaleString()}
                </p>
              </div>
            </div>
            
            {/* Chart */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <h2 className="text-xl font-semibold mb-4 text-white">Portfolio Growth Over Time</h2>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#9CA3AF"
                    tick={{ fontSize: 12 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    stroke="#9CA3AF"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1F2937', 
                      border: '1px solid #374151',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#F3F4F6' }}
                    formatter={(value, name) => {
                      if (name === 'invested' || name === 'value') {
                        return `$${value.toLocaleString()}`;
                      }
                      return value;
                    }}
                  />
                  <Legend 
                    wrapperStyle={{ color: '#9CA3AF' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="invested" 
                    stroke="#6B7280" 
                    strokeWidth={2}
                    name="Total Invested"
                    dot={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#F97316" 
                    strokeWidth={2}
                    name="Portfolio Value"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HindsightSimulator;